import * as _ from "lodash";
import { createSelector } from "reselect";
import { ICanvasEditorState } from "../interfaces/model/CanvasEditorState";
import { GeometryTypes } from "../interfaces/model/enumerations/index";
import { IPage } from "../interfaces/model/Page";
import { IGroupShape } from "../interfaces/model/PageObjects";
import { IShapeProperties } from "../interfaces/model/properties";
import { PageObjectType } from "../interfaces/model/Types";
import { getSelectedPage } from "./page";

/**
 * Get selected page objects indices from state
 * @param state {ICanvasEditorState} - The redux state
 * @returns {number[]} selected objects index array. Empty array if there is no selected object
 */
const selections = (state: ICanvasEditorState) => state.selection.present.selectedPageObjects;

/**
 * get selected object's group index from state
 * @param state {ICanvasEditorState} - The redux state
 * @returns {number} selected object in the group.  -1 if no selected object in group
 */
const groupSelection = (state: ICanvasEditorState) => state.selection.present.groupSelectionIndices;

/**
 * Get selected objects indices
 * @param selections {number[]}
 * @return {number[]}
 */
const selectedObjectsIndices = createSelector([selections], (selectionsList) => {
	return selectionsList;
});

/**
 * Get selected group object index
 * @param groupSelection {number}
 * @return {number[]}
 */
const groupSelectionIndices = createSelector([groupSelection], (index) => {
	return index;
});

/**
 * Get all selected objects, including objects in groups and sub-groups.
 * @param pageObject contains data for a canvas page object (which might have sub-objects).
 * @return page objects and sub-page objects
 */
export const getAllSelectedObjects = (pageObject: any): any => {
	let allSelectedObjects: any[] = [];

	if (pageObject.hasOwnProperty("childObjects")) {
		pageObject.childObjects.forEach((child: any) => {
			allSelectedObjects = allSelectedObjects.concat(getAllSelectedObjects(child));
		});
	} else {
		allSelectedObjects.push(pageObject);
	}
	return allSelectedObjects;
};

/**
 * Get selected objects from page
 * @param page {IPage} - Current selected page
 * @param objectIndices {number[]} - selected objects indices
 * @param groupIndex {number} - selected object in group
 * @returns {T[] | null} Object with selected objects array or null
 */
const _getSelectedObjects = (page: IPage | null, objectIndices: number[], groupIndex: number):
        PageObjectType[] | null => {
	const selectedObjects: PageObjectType[] = [] as PageObjectType[];
	if (page) {
		for (const index of objectIndices) {
			selectedObjects.push(page.pageObjects[index]);
		}
		return selectedObjects ;
	}
	return null;
};

export const getSelectedPageObjects = createSelector([getSelectedPage, selectedObjectsIndices, groupSelectionIndices] as
    any, _getSelectedObjects);

/**
 * Get selected objects from page
 * @param page {IPage} - Current selected page
 * @param objectIndices {number[]} - selected objects indices
 * @param groupIndex {number} - selected object in group
 * @returns {T[] | null} Object with selected objects array or null
 */
const _getSelectedBaseObjects = (page: IPage | null, objectIndices: number[], groupIndices: number[]):
        PageObjectType[] | null => {
	let selectedObjects: PageObjectType[] = [] as PageObjectType[];
	objectIndices.sort();
	if (page) {
		for (const index of objectIndices) {
			selectedObjects.push(page.pageObjects[index]);
		}
		if (groupIndices !== undefined &&
            groupIndices.length &&
            selectedObjects.length === 1) {
			const groupObject = selectedObjects[0] as IGroupShape;
			selectedObjects = [];
			for (const index of groupIndices) {
				selectedObjects.push(groupObject.childObjects[index]);
			}
		}
		return selectedObjects ;
	}
	return null;
};

export const getSelectedBaseObjects = createSelector([getSelectedPage, selectedObjectsIndices, groupSelectionIndices] as
    any, _getSelectedBaseObjects);

/**
 * Get selected GeometryTypes.CUSTOM Object from page
 * @param pageObjects {PageObjectType[]} - current selected Objects
 * @returns {T[] | null} Object with selected objects array or null
 */
const _getSelectedCustomShapes = (pageObjects: PageObjectType[] | null): PageObjectType[] | null => {
	if (pageObjects) {
		return _.filter(pageObjects, (po) => {
			const spPr = po.spPr as IShapeProperties;
			return spPr.geometry && spPr.geometry.type === GeometryTypes.CUSTOM;
		});
	}
	return null;
};

export const getSelectedCustomShapes = createSelector([getSelectedPageObjects], _getSelectedCustomShapes);
